import { createRef, useCallback } from 'react'

import ReCAPTCHA from 'react-google-recaptcha'

import { EMAIL_PATTERN } from '@bettermode/common/utils'
import {
  Member,
  ModerationEntityType,
  Post,
  ReportCategory,
} from '@tribeplatform/gql-client/types'
import {
  useAuthMember,
  useCreateModerationReport,
  useNetwork,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { RadioGroup } from '@tribeplatform/react-ui-kit/RadioGroup'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { ErrorBoundary } from '../common/components/ErrorBoundary/ErrorBoundary.js'
import { logger } from '../common/index.js'
import { Form } from '../Form/index.js'
import { useI18n, useStaticIntl, T } from '../i18n/index.js'
import { I18nShape } from '../i18n/types.js'

import { useReportCategoryLabels } from './reportCategoryLabels.js'

const titleFactory = (
  { $t }: I18nShape,
  entityType: ModerationEntityType,
): string => {
  switch (entityType) {
    case ModerationEntityType.MEMBER:
      return $t({
        defaultMessage: 'Report {MEMBER}',
        id: 'Member.Report',
      })
    case ModerationEntityType.POST:
      return $t({
        defaultMessage: 'Report post',
        id: 'Post.Report',
      })
    default: {
      const exhaustiveCheck: never = entityType
      return exhaustiveCheck
    }
  }
}

const descriptionLabelFactory = (
  $t: I18nShape['$t'],
  reportCategory: ReportCategory,
): string => {
  switch (reportCategory) {
    case ReportCategory.ILLEGAL_CONTENT:
      return $t({
        defaultMessage: 'Why is this content illegal? (required)',
        id: 'ReportCategory.IllegalContent.Description.Required',
      })
    case ReportCategory.OTHER:
      return $t({
        defaultMessage: 'Explain this report (required)',
        id: 'ReportCategory.Description.Required',
      })
    default:
      return $t({
        defaultMessage: 'Explain this report (optional)',
        id: 'ReportCategory.Description',
      })
  }
}

type ReportFormValues = {
  reportCategory: ReportCategory
  description?: string
  reporterName?: string
  reporterEmail?: string
  goodFaithConfirmed?: boolean
}

export type ReportModalProps = {
  entityType: ModerationEntityType
  entity: Post | Member
  open: boolean
  onClose: () => void
}

export const ReportModal = ({
  entityType,
  entity,
  open,
  onClose,
}: ReportModalProps) => {
  const { $t } = useI18n()
  const categoryLabels = useReportCategoryLabels()
  const title = useStaticIntl(titleFactory, entityType)
  const { mutateAsync: createModerationReport, isLoading } =
    useCreateModerationReport()
  const { isGuest } = useAuthMember()
  const { data: network } = useNetwork()

  const captchaEnabled = network?.settings?.captcha?.enabled
  const captchaSiteKey = network?.settings?.captcha?.siteKey
  const shouldShowCaptcha = isGuest && captchaEnabled && captchaSiteKey

  const reCaptchaRef = createRef<ReCAPTCHA.ReCAPTCHA>()

  const getCaptchaToken = useCallback(async () => {
    if (!shouldShowCaptcha) {
      return undefined
    }

    const reCaptcha = reCaptchaRef.current
    if (!reCaptcha) {
      return undefined
    }

    try {
      const captchaToken = await reCaptcha.executeAsync()
      try {
        reCaptcha.reset()
      } catch (resetError) {
        logger.error('Error resetting reCAPTCHA:', resetError)
      }
      return captchaToken
    } catch (error) {
      logger.error('Error executing reCAPTCHA:', error)
      throw new Error(
        $t({
          defaultMessage: 'Failed to verify captcha. Please try again.',
          id: 'Captcha.Error.Execution',
        }),
      )
    }
  }, [shouldShowCaptcha, reCaptchaRef, $t])

  if (!entity?.id || !entityType) {
    return null
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header title={title} />
      <Modal.Content>
        <Form<ReportFormValues>
          className="flex flex-col space-y-5"
          onSubmit={async ({
            reportCategory,
            description,
            reporterName,
            reporterEmail,
            goodFaithConfirmed,
          }) => {
            const captchaToken = await getCaptchaToken()
            const isIllegal = reportCategory === ReportCategory.ILLEGAL_CONTENT
            const sendReporterContact = isIllegal && isGuest
            const name = sendReporterContact ? reporterName?.trim() : undefined
            const email = sendReporterContact
              ? reporterEmail?.trim()
              : undefined

            await createModerationReport({
              input: {
                entityType,
                entityId: entity?.id,
                reportCategory,
                description,
                spaceId: (entity as Post)?.spaceId,
                ...(captchaToken && { captchaToken }),
                ...(name ? { reporterName: name } : {}),
                ...(email ? { reporterEmail: email } : {}),
                ...(isIllegal
                  ? { goodFaithConfirmed: !!goodFaithConfirmed }
                  : {}),
              },
            })
            toast({
              title: $t({
                defaultMessage: 'Report submitted',
                id: 'Toast.Title',
              }),
              description: $t({
                defaultMessage:
                  'Thanks for making the {COMMUNITY} a better place.',
                id: 'Toast.Description',
              }),
            })
            onClose()
          }}
        >
          {({ watch }) => {
            const [reportCategory] = watch(['reportCategory'])
            const isIllegalContent =
              reportCategory === ReportCategory.ILLEGAL_CONTENT
            return (
              <>
                <Form.RadioGroup
                  name="reportCategory"
                  validation={{
                    required: $t({
                      defaultMessage: 'Please pick a category',
                      id: 'ReportCategory.Title',
                    }),
                  }}
                >
                  <RadioGroup.Items>
                    <RadioGroup.Item
                      value={ReportCategory.HARASSMENT}
                      title={categoryLabels[ReportCategory.HARASSMENT]}
                      description={$t({
                        defaultMessage:
                          'Harassing or threatening a person or group',
                        id: 'ReportCategory.Harassment.Description',
                      })}
                    />
                    <RadioGroup.Item
                      value={ReportCategory.PRIVACY_VIOLATION}
                      title={categoryLabels[ReportCategory.PRIVACY_VIOLATION]}
                      description={$t({
                        defaultMessage:
                          "Sharing someone's personal information without consent, such as home address, phone number, or private messages",
                        id: 'ReportCategory.Privacy.Description',
                      })}
                    />
                    <RadioGroup.Item
                      value={ReportCategory.SPAM}
                      title={categoryLabels[ReportCategory.SPAM]}
                      description={$t({
                        defaultMessage:
                          'Undisclosed promotion for a link or selling illegal goods, money scams, etc.',
                        id: 'ReportCategory.Spam.Description',
                      })}
                    />
                    <RadioGroup.Item
                      value={ReportCategory.MISINFORMATION}
                      title={categoryLabels[ReportCategory.MISINFORMATION]}
                      description={$t({
                        defaultMessage:
                          'Substantially incorrect and/or incorrect primary conclusions',
                        id: 'ReportCategory.Misinformation.Description',
                      })}
                    />
                    <RadioGroup.Item
                      value={ReportCategory.VIOLENCE}
                      title={categoryLabels[ReportCategory.VIOLENCE]}
                      description={$t({
                        defaultMessage:
                          'Glorifying violence including self-harm or intent to seriously harm others',
                        id: 'ReportCategory.Harmful.Description',
                      })}
                    />
                    <RadioGroup.Item
                      value={ReportCategory.NUDITY}
                      title={categoryLabels[ReportCategory.NUDITY]}
                      description={$t({
                        defaultMessage: 'Nudity or sexual content',
                        id: 'ReportCategory.Adult.Description',
                      })}
                    />
                    <RadioGroup.Item
                      value={ReportCategory.COPYRIGHT_INFRINGEMENT}
                      title={
                        categoryLabels[ReportCategory.COPYRIGHT_INFRINGEMENT]
                      }
                      description={$t({
                        defaultMessage:
                          "Content that uses someone else's copyrighted work, brand, or logo without permission",
                        id: 'ReportCategory.Copyright.Description',
                      })}
                    />
                    <RadioGroup.Item
                      value={ReportCategory.ILLEGAL_CONTENT}
                      title={categoryLabels[ReportCategory.ILLEGAL_CONTENT]}
                      description={$t({
                        defaultMessage:
                          'Content that breaks the law, e.g. defamation or illegal goods',
                        id: 'ReportCategory.IllegalContent.Description',
                      })}
                    />
                    <RadioGroup.Item
                      value={ReportCategory.OTHER}
                      title={categoryLabels[ReportCategory.OTHER]}
                    />
                  </RadioGroup.Items>
                </Form.RadioGroup>
                {reportCategory && (
                  <Form.Textarea
                    validation={{
                      validate: (value: string) => {
                        if (value?.trim()) {
                          return true
                        }
                        if (reportCategory === ReportCategory.OTHER) {
                          return $t({
                            defaultMessage: 'Please provide a description',
                            id: 'ReportCategory.Description.Required.Error',
                          })
                        }
                        if (reportCategory === ReportCategory.ILLEGAL_CONTENT) {
                          return $t({
                            defaultMessage:
                              'Please explain why this content is illegal',
                            id: 'ReportCategory.IllegalContent.Description.Required.Error',
                          })
                        }
                        return true
                      },
                    }}
                    name="description"
                    label={descriptionLabelFactory($t, reportCategory)}
                  />
                )}
                {isIllegalContent && (
                  <>
                    {isGuest && (
                      <>
                        <Form.Input
                          name="reporterName"
                          label={$t({
                            defaultMessage: 'Your name (optional)',
                            id: 'ReportModal.Guest.Name',
                          })}
                        />
                        <Form.Input
                          name="reporterEmail"
                          type="email"
                          label={$t({
                            defaultMessage: 'Your email (optional)',
                            id: 'ReportModal.Guest.Email',
                          })}
                          helperText={$t({
                            defaultMessage:
                              'Only used to send you a confirmation and the outcome of this report.',
                            id: 'ReportModal.Guest.Email.Helper',
                          })}
                          validation={{
                            pattern: {
                              value: EMAIL_PATTERN,
                              message: $t({
                                defaultMessage:
                                  'Please enter a valid email address',
                                id: 'ReportModal.Guest.Email.Invalid',
                              }),
                            },
                          }}
                        />
                      </>
                    )}
                    <Form.Checkbox
                      name="goodFaithConfirmed"
                      label={$t({
                        defaultMessage:
                          'I believe in good faith that the information in this report is accurate and complete.',
                        id: 'ReportModal.GoodFaith',
                      })}
                      validation={{
                        required: $t({
                          defaultMessage:
                            'Please confirm this statement to submit the report',
                          id: 'ReportModal.GoodFaith.Error',
                        }),
                      }}
                    />
                  </>
                )}
                <Form.Actions>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    loading={isLoading}
                  >
                    <T defaultMessage="Report" id="Generics.Report.Verb" />
                  </Button>
                  <Button variant="secondaryNeutral" onClick={onClose}>
                    <T defaultMessage="Cancel" id="Generics.Cancel" />
                  </Button>
                </Form.Actions>
              </>
            )
          }}
        </Form>
        {shouldShowCaptcha && (
          <ErrorBoundary fallback={null}>
            {/* @ts-expect-error - ReCAPTCHA component has incorrect TypeScript type definitions */}
            <ReCAPTCHA
              ref={reCaptchaRef}
              sitekey={captchaSiteKey}
              size="invisible"
            />
          </ErrorBoundary>
        )}
      </Modal.Content>
    </Modal>
  )
}
